import { pure } from '@core/helpers/pure';
import { PolymorpheusOutlet } from '@taiga-ui/polymorpheus';
import { TuiAvatar, TuiPreview, TuiPreviewDialogService } from '@taiga-ui/kit';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  OutputEmitterRef,
  OutputRefSubscription,
  Renderer2,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { tuiClamp, TuiSwipe, TuiSwipeEvent } from '@taiga-ui/cdk';
import { TuiDialogContext, TuiButton, TuiHint } from '@taiga-ui/core';
import { isImageByFilename } from '@core/helpers';
import type { Nullable } from '@core/helpers';
import { RequestFormFilesTranslations } from '@core/types';
import type {
  RequestFile,
  RequestFileCategory,
  RequestFormFilesTranslationsPayload,
} from '@core/types';
import { Subscriber, take } from 'rxjs';
import { environment } from '@environment/environment';
import type { FileGroup } from '@features/requests/request-form/components/request-form-summary/types';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PolymorpheusContent } from '@taiga-ui/polymorpheus';
import { FileDownloadService, XAlertService } from '@core/services';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';
import { RequestFilesService } from '@core/services/request/request-files.service';
import type { RequestModel } from '@core/models';
import { TranslatePipe } from '@ngx-translate/core';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import { DeviceDetectorService } from '@core/services/device-detector/device-detector.service';

@Component({
  selector: 'x-image-preview',
  imports: [
    CommonModule,
    TuiPreview,
    TuiSwipe,
    TuiAvatar,
    PolymorpheusOutlet,
    TuiButton,
    TranslatePipe,
    TuiHint,
  ],
  templateUrl: './image-preview.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImagePreviewComponent implements OnInit, OnDestroy {
  $files = input.required<Nullable<RequestFile[]>>({
    alias: 'files',
  });

  $viewOnly = input<boolean>(false, { alias: 'viewOnly' });

  $showPreviewImageData = input.required<
    OutputEmitterRef<[RequestFile, FileGroup, index: number]>
  >({
    alias: 'showPreviewImageData',
  });

  $request = input.required<RequestModel>({ alias: 'request' });

  private readonly destroyRef = inject(DestroyRef);
  private readonly previewDialogService: TuiPreviewDialogService = inject(
    TuiPreviewDialogService,
  );
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);
  private readonly xAlertService = inject(XAlertService);
  readonly permissionService = inject(OutkeepPermissionService);
  private readonly requestFilesService = inject(RequestFilesService);
  private readonly renderer: Renderer2 = inject(Renderer2);
  private readonly fileDownloadService = inject(FileDownloadService);
  public readonly deviceService = inject(DeviceDetectorService);

  @ViewChild('imagePreview')
  readonly imagePreview?: TemplateRef<TuiDialogContext>;
  public currentPreviewImage: Nullable<RequestFile> = null;
  public previewImageIndex = 0;
  public previewImagesLength = 0;
  public previewFileCatId: Nullable<number> = null;
  public currentPreviewImageCatName: Nullable<string> = null;

  private translates: Partial<RequestFormFilesTranslationsPayload> = {
    [RequestFormFilesTranslations.DialogDeleteFile]:
      'Вы действительно хотите удалить файл ',
    [RequestFormFilesTranslations.DialogDeleteFileTitle]: 'Удаление файла',
    [RequestFormFilesTranslations.SuccessDeletingFile]: 'Файл успешно удален',
    [RequestFormFilesTranslations.ErrorDeletingFile]: 'Ошибка удаления файла',
    [RequestFormFilesTranslations.DialogRequestToCancelDeleteTitle]:
      'Запрос на отмену удаления файла',
    [RequestFormFilesTranslations.DialogRequestToCancelDeleteMsg]:
      'Вы действительно хотите отменить заявку на удаление файла',
    [RequestFormFilesTranslations.DialogRequestToDeleteTitle]:
      'Запрос на удаление файла',
    [RequestFormFilesTranslations.DialogRequestToDeleteMsg1]:
      'Вы действительно хотите создать заявку на удаление файла',
    [RequestFormFilesTranslations.DialogRequestToDeleteMsg2]:
      'Будет создан запрос на удаление файла. После одобрение администратором файл будет удален.',
    [RequestFormFilesTranslations.SuccessRequestToCancelDelete]:
      'Заявка на отмену удаления файла успешно создана',
    [RequestFormFilesTranslations.SuccessRequestToDelete]:
      'Заявка на удаление файла успешно создана',
    [RequestFormFilesTranslations.ErrorRequestToCancelDelete]:
      'Ошибка создания запроса на отмену удаления файла',
    [RequestFormFilesTranslations.ErrorRequestToDelete]:
      'Ошибка создания запроса на удаление файла',
    [RequestFormFilesTranslations.VinCopied]: 'VIN скопирован в буфер обмена',
    [RequestFormFilesTranslations.ErrorCopiedVin]:
      'Ошибка копирования VIN в буфер обмена',
  };

  private emitterSub: Nullable<OutputRefSubscription> = null;

  constructor() {
    effect(() => {
      const emitter = this.$showPreviewImageData();

      if (emitter && this.$files() && !this.emitterSub) {
        this.emitterSub = emitter.subscribe(([file, group, index]) => {
          if (file && group) {
            this.showImagePreview(file, group, index);
          }
        });
      }
    });
  }

  ngOnInit(): void {
    this.getTranslations();
  }

  ngOnDestroy(): void {
    if (this.emitterSub) {
      this.emitterSub.unsubscribe();
      this.emitterSub = null;
    }
  }

  private getTranslations() {
    this.outkeepTranslateService
      .translate(Object.values(RequestFormFilesTranslations))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(translates => (this.translates = translates));
  }

  private showImagePreview(file: RequestFile, group: FileGroup, index: number) {
    this.currentPreviewImage = file;
    this.currentPreviewImageCatName = group.name;
    this.previewImagesLength = group.files.length;
    this.previewImageIndex = index;
    this.previewFileCatId = group.id;
    this.previewDialogService
      .open(this.imagePreview)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  private getCurrentPreviewImageInternal(): RequestFile {
    const files = this.$files()!;

    // If previewFileCatId is 0 or null, show all files (mixed categories)
    // Otherwise, filter by specific category ID
    if (this.previewFileCatId === 0 || this.previewFileCatId === null) {
      return files[this.previewImageIndex]!;
    }

    // Filter by category for single-category views
    return files
      .filter(
        file =>
          (file.catId as RequestFileCategory)?.id === this.previewFileCatId!,
      )
      .find((_, index) => index === this.previewImageIndex)!;
  }

  private createLinkAndClick(
    href: string,
    filename: string,
    withoutDownload = false,
  ): void {
    this.fileDownloadService.downloadFile(
      href,
      filename,
      withoutDownload,
      this.renderer,
    );
  }

  public backdropClickClose(
    event: Event,
    imagePreviewObserver: Subscriber<any>,
  ) {
    if ((event.target as HTMLElement).tagName === 'TUI-PREVIEW') {
      this.closeImagePreview(imagePreviewObserver);
    }
  }

  public closeImagePreview(imagePreviewObserver: Subscriber<any>) {
    this.currentPreviewImage = null;
    imagePreviewObserver.complete();
  }

  public downloadPreviewImage() {
    this.createLinkAndClick(
      environment.downloadFilesUrl + this.currentPreviewImage!.path,
      this.currentPreviewImage!.filename,
    );
  }

  public onSwipePreviewImage(swipe: TuiSwipeEvent): void {
    if (swipe.direction === 'left') {
      this.previewImageIndex = tuiClamp(
        this.previewImageIndex + 1,
        0,
        this.previewImagesLength - 1,
      );
    }

    if (swipe.direction === 'right') {
      this.previewImageIndex = tuiClamp(
        this.previewImageIndex - 1,
        0,
        this.previewImagesLength - 1,
      );
    }
  }

  public getCurrentPreviewImage(): RequestFile {
    this.currentPreviewImage = this.getCurrentPreviewImageInternal();

    return this.currentPreviewImage;
  }

  public getCurrentPreviewImageSrc(): PolymorpheusContent {
    this.currentPreviewImage = this.getCurrentPreviewImageInternal();

    return this.getFileUrl(this.currentPreviewImage);
  }

  @pure
  public getFileUrl(file: RequestFile) {
    return environment.staticUrl + file.path;
  }

  @pure
  public fileIsImage(file: RequestFile) {
    return isImageByFilename(file.filename);
  }

  copyVinToClipboard(vin: string) {
    navigator.clipboard
      .writeText(vin)
      .then(() => {
        this.xAlertService.success(
          vin,
          this.translates[RequestFormFilesTranslations.VinCopied],
        );
      })
      .catch(err => {
        this.xAlertService.error(
          this.translates[RequestFormFilesTranslations.ErrorCopiedVin]!,
        );
        console.error('Failed to copy VIN:', err);
      });
  }

  public deleteFile(
    file: RequestFile,
    imagePreviewObserver: Subscriber<any>,
  ): void {
    const msg = `
        ${
          this.translates[RequestFormFilesTranslations.DialogDeleteFile]
        } <b>"${file.filename}</b>?
        `;

    this.xAlertService
      .showDialog(
        this.translates[RequestFormFilesTranslations.DialogDeleteFileTitle]!,
        msg,
      )
      .pipe(take(1))
      .subscribe(res => {
        if (!res) {
          return;
        }
        this.requestFilesService
          .deleteRequestFile$(file)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.requestFilesService.refreshEntities();
              this.xAlertService.success(
                this.translates[
                  RequestFormFilesTranslations.SuccessDeletingFile
                ]!,
              );
              this.showPrevImageOrClosePreview(imagePreviewObserver);
            },
            error: err => {
              this.xAlertService.error(
                this.translates[
                  RequestFormFilesTranslations.ErrorDeletingFile
                ]!,
              );
              this.requestFilesService.refreshEntities();
              imagePreviewObserver?.complete();
              console.error('File delete failed', err);
            },
          });
      });
  }

  createFileDeleteRequest(
    file: RequestFile,
    imagePreviewObserver: Subscriber<any>,
  ): void {
    const restore = this.fileIsRequestedToDelete(file);
    let msg: string;
    let title: string;

    if (restore) {
      title =
        this.translates[
          RequestFormFilesTranslations.DialogRequestToCancelDeleteTitle
        ]!;
      msg =
        this.translates[
          RequestFormFilesTranslations.DialogRequestToCancelDeleteMsg
        ] + `<b>"${file.filename}</b>?`;
    } else {
      title =
        this.translates[
          RequestFormFilesTranslations.DialogRequestToDeleteTitle
        ]!;
      msg =
        this.translates[
          RequestFormFilesTranslations.DialogRequestToDeleteMsg1
        ] +
        `<b>"${file.filename}</b>"? ` +
        this.translates[RequestFormFilesTranslations.DialogRequestToDeleteMsg2];
    }

    this.xAlertService
      .showDialog(title, msg)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => {
        if (!res) {
          return;
        }
        this.requestFilesService
          .createFileDeleteRequest$(this.$request().id, file)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.xAlertService.success(
                restore
                  ? this.translates[
                      RequestFormFilesTranslations.SuccessRequestToCancelDelete
                    ]!
                  : this.translates[
                      RequestFormFilesTranslations.SuccessRequestToDelete
                    ]!,
              );
              this.requestFilesService.refreshEntities();
              imagePreviewObserver?.complete();
            },
            error: err => {
              let msg: string;

              if (restore) {
                msg =
                  this.translates[
                    RequestFormFilesTranslations.ErrorRequestToCancelDelete
                  ]!;
              } else {
                msg =
                  this.translates[
                    RequestFormFilesTranslations.ErrorRequestToDelete
                  ]!;
              }
              this.xAlertService.error(msg);
              console.error('File delete request failed', err);
              imagePreviewObserver?.complete();
            },
          });
      });
  }

  private showPrevImageOrClosePreview(imagePreviewObserver: Subscriber<any>) {
    this.previewImagesLength--;
    if (this.previewImagesLength < 1) {
      imagePreviewObserver.complete();
    } else {
      if (this.previewImageIndex === 0) {
        this.previewImageIndex = this.previewImagesLength - 1;
      } else {
        --this.previewImageIndex;
      }

      this.currentPreviewImage = this.getCurrentPreviewImageInternal();
    }
  }

  @pure
  private fileIsRequestedToDelete(file: RequestFile) {
    return file.deleteRequestUserId !== null;
  }
}
